// Croatian text-to-speech. On Capacitor native, uses the device's built-in
// TTS engine via @capacitor-community/text-to-speech (more reliable than the
// WebView's speechSynthesis, which is inconsistent on iOS). On web, falls
// back to the Web Speech API.

import { Capacitor } from '@capacitor/core';
import { duck, unduck } from './music.js';

const isNative = () => {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
};

// Lazy-loaded native plugin so the web bundle doesn't pay the cost.
let nativeTTSPromise = null;
function getNativeTTS() {
  if (!isNative()) return Promise.resolve(null);
  if (!nativeTTSPromise) {
    nativeTTSPromise = import('@capacitor-community/text-to-speech')
      .then((mod) => mod.TextToSpeech)
      .catch(() => null);
  }
  return nativeTTSPromise;
}

// ─── Web Speech API fallback ─────────────────────────────────────────────
let cachedVoice = null;
function pickCroatianVoice() {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  const voices = window.speechSynthesis.getVoices();
  if (!voices.length) return null;
  const exact = voices.find((v) => v.lang === 'hr-HR' || v.lang === 'hr_HR');
  const partial = voices.find((v) => v.lang && v.lang.toLowerCase().startsWith('hr'));
  return exact || partial || null;
}

if (typeof window !== 'undefined' && window.speechSynthesis) {
  window.speechSynthesis.onvoiceschanged = () => {
    cachedVoice = pickCroatianVoice();
  };
}

function webSpeak(text, rate) {
  if (typeof window === 'undefined' || !window.speechSynthesis) return false;
  try {
    window.speechSynthesis.cancel();
    if (!cachedVoice) cachedVoice = pickCroatianVoice();
    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = 'hr-HR';
    utter.rate = rate;
    if (cachedVoice) utter.voice = cachedVoice;
    let restored = false;
    const restore = () => { if (!restored) { restored = true; unduck(); } };
    utter.onend = restore;
    utter.onerror = restore;
    duck();
    window.speechSynthesis.speak(utter);
    return true;
  } catch {
    return false;
  }
}

// ─── Public API ──────────────────────────────────────────────────────────
export async function speak(text, { rate = 0.9 } = {}) {
  const TTS = await getNativeTTS();
  if (TTS) {
    try {
      await TTS.stop().catch(() => {});
      duck();
      try {
        await TTS.speak({
          text,
          lang: 'hr-HR',
          rate,
          pitch: 1.0,
          volume: 1.0,
          category: 'ambient',
        });
      } finally {
        unduck();
      }
      return true;
    } catch {
      // Fall through to web on plugin error.
    }
  }
  return webSpeak(text, rate);
}

// Returns true if we expect speech to work. On native we assume yes (the OS
// always has SOME TTS); on web we check for a Croatian voice.
export function hasCroatianVoice() {
  if (isNative()) return true;
  if (typeof window === 'undefined' || !window.speechSynthesis) return false;
  if (!cachedVoice) cachedVoice = pickCroatianVoice();
  return Boolean(cachedVoice);
}
